import React, { useEffect, useState } from "react";
import { toast } from "../../../utils/notify";
import ApiLoader from "../../ui/ApiLoader";
import { useAuth } from "../../../context/AuthContext";
import {
  fetchAssets,
} from "../../../services/axios/AuthApis/DashboardApis";
import { DataTable } from "../../common/DataTable";

const Assets = ({ activeTab }) => {
  const { user, isPlatformAdmin } = useAuth();
  const [assets, setAssets] = useState([]);

  const [tableLoading, setTableLoading] = useState(false);

const loadTable = async () => {
  try {
    setTableLoading(true);

    // Platform admin sees all data; customer admin is scoped to their own.
    const customerId = isPlatformAdmin
      ? ""
      : user?.customer_id || user?.id || user?._id || user?.customer?.id || "";

    // sensitiveData=true so the backend also syncs sensitive assets
    // into DSPMDataStore as a side effect of this discovery run.
    const res = await fetchAssets(customerId, true);

    setAssets(Array.isArray(res) ? res : res?.data || []);
  } catch (err) {
    console.log(err);
    if (err.code === "ECONNABORTED") {
      toast.error("Loading assets timed out. Please try again.");
    } else {
      toast.error("Unable to load assets");
    }
  } finally {
    setTableLoading(false);
  }
};



 useEffect(() => {
   if (activeTab === "assets") {
     const load = async () => {
       await loadTable();
     };

     load();
   }
 }, [activeTab]);

  const uniqueValues = (key) => [...new Set(assets.map((a) => a[key]).filter(Boolean))];

  const columns = [
    {
      id: "company_name",
      accessorFn: (a) => a.company_name,
      header: "Company",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Company", placeholder: "Search company" } },
      cell: ({ row }) => <span>{row.original.company_name || "-"}</span>,
    },
    {
      id: "connector_name",
      accessorFn: (a) => a.connector_name,
      header: "Connector Name",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Connector",
          placeholder: "All connectors",
          options: uniqueValues("connector_name").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.connector_name || "-"}</span>,
    },
    {
      id: "type",
      accessorFn: (a) => a.type,
      header: "Type",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Type",
          placeholder: "All types",
          options: uniqueValues("type").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.type || "-"}</span>,
    },
    {
      id: "criticality",
      accessorFn: (a) => a.criticality,
      header: "Criticality",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Criticality",
          placeholder: "All criticality",
          options: uniqueValues("criticality").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.criticality || "-"}</span>,
    },
    {
      id: "connection_status",
      accessorFn: (a) => a.connection_status,
      header: "Connection Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: uniqueValues("connection_status").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.connection_status || "-"}</span>,
    },
    {
      id: "last_synced",
      accessorFn: (a) => a.last_synced,
      header: "Last Synced",
      enableSorting: false,
      cell: ({ row }) => (
        <span>{row.original.last_synced ? new Date(row.original.last_synced).toLocaleString() : "-"}</span>
      ),
    },
  ];

  if (tableLoading) {
    return (
      <ApiLoader
        variant="spinner"
        label="Loading Assets..."
        className="py-20"
      />
    );
  }

  return (
    <>
      {activeTab === "assets" && (
        <>
          <h2 className="text-2xl font-bold mb-6">Assets</h2>

          {/* Summary Cards */}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Total Assets</p>

              <h2 className="text-3xl font-bold mt-2">{assets.length}</h2>
            </div>
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">AWS Assets</p>
              <h2 className="text-3xl font-bold mt-2">
                {
                  assets.filter(
                    (item) => item.connector_name?.toLowerCase() === "aws",
                  ).length
                }
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Azure Assets</p>
              <h2 className="text-3xl font-bold mt-2">
                {
                  assets.filter(
                    (item) => item.connector_name?.toLowerCase() === "azure",
                  ).length
                }
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">GCP Assets</p>
              <h2 className="text-3xl font-bold mt-2">
                {
                  assets.filter(
                    (item) => item.connector_name?.toLowerCase() === "gcp",
                  ).length
                }
              </h2>
            </div>
          </div>

          {/* Asset Table */}

          <div className="relative bg-white rounded-xl shadow mt-6 p-6">
            <div className="flex justify-between mb-5">
              <h2 className="text-xl font-bold">Asset Inventory</h2>
            </div>
            <DataTable
              columns={columns}
              data={assets}
              getRowId={(asset, index) => `${asset.id}-${asset.type}-${index}`}
              loading={tableLoading}
              emptyMessage="No Assets Found"
              enableColumnVisibility={false}
              enableColumnResizing
              enableColumnFilters
              maxBodyHeight="65vh"
            />
          </div>
        </>
      )}
    </>
  );
};

export default Assets;
